import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { COACHING_TABS, DEFAULT_COACHING_TAB, LAST_TAB_STORAGE_KEY, TAB_QUERY_PARAM } from '../constants';

const findTab = (tabs, path) => tabs.find((tab) => tab.path === path) || null;

const readStoredTab = (tabs) => {
  try {
    return findTab(tabs, window.localStorage.getItem(LAST_TAB_STORAGE_KEY));
  } catch {
    return null;
  }
};

const storeTab = (tab) => {
  try {
    window.localStorage.setItem(LAST_TAB_STORAGE_KEY, tab.path);
  } catch {
    // Storage can be unavailable (private mode, quota); the URL still carries the tab.
  }
};

const readUrlTab = (tabs, search) => findTab(tabs, new URLSearchParams(search).get(TAB_QUERY_PARAM));

// Owns the active header tab. Initial tab: `?tab=` in the URL, then the last tab stored in
// localStorage, then the default. The URL and the storage are kept in sync with the state, and a
// `?tab=` change coming from the browser (back/forward) updates the state. Same approach as the
// video module (`Modules/video/views/main`), without Redux.
// `tabs` are the catalog entries the user's role may open (see permissions.js): a `?tab=` or a
// stored tab outside them is ignored like an unknown value.
// `enabled: false` (user not allowed yet, or redirected away) keeps the hook from touching the URL
// or the storage, so it never overrides the view's own `navigate('/error')`.
export const useCoachingTab = ({ enabled = true, tabs = COACHING_TABS } = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(
    () => readUrlTab(tabs, location.search) || readStoredTab(tabs) || tabs[0] || DEFAULT_COACHING_TAB,
  );
  // Set when the state change comes from the URL, so the state -> URL effect does not rewrite it.
  const syncingFromUrlRef = useRef(false);

  useEffect(() => {
    // The allowed tabs changed (e.g. the user loaded after mount): keep the state inside them.
    if (tabs.length && !tabs.includes(activeTab)) {
      setActiveTab(tabs[0]);
    }
    // Only reacts to `tabs` changes; `activeTab` is read through the closure on purpose.
  }, [tabs]);

  useEffect(() => {
    const urlTab = readUrlTab(tabs, location.search);
    if (urlTab && urlTab.path !== activeTab.path) {
      syncingFromUrlRef.current = true;
      setActiveTab(urlTab);
    }
    // Only reacts to URL changes; `activeTab` is read through the closure on purpose.
  }, [location.search]);

  useEffect(() => {
    if (!enabled) return;

    storeTab(activeTab);

    if (syncingFromUrlRef.current) {
      syncingFromUrlRef.current = false;
      return;
    }

    const params = new URLSearchParams(location.search);
    if (params.get(TAB_QUERY_PARAM) !== activeTab.path) {
      params.set(TAB_QUERY_PARAM, activeTab.path);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [enabled, activeTab, navigate, location.pathname, location.search]);

  // Normalizes to the catalog entry so `activeIndex` works with any object carrying a known path.
  const selectTab = useCallback(
    (tab) => {
      const known = findTab(tabs, tab?.path);
      if (known) {
        setActiveTab(known);
      }
    },
    [tabs],
  );

  return {
    activeTab,
    activeIndex: tabs.indexOf(activeTab),
    selectTab,
  };
};
